import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  MapPin, 
  Phone, 
  Mail, 
  ChevronRight, 
  Youtube, 
  Instagram, 
  Facebook,
  ShieldCheck
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const TELEGRAM_LINK = "https://t.me/+QOePMys7V481NmZl";

const Footer = ({ SC: propSC }) => {
  const [sc, setSc] = useState(propSC || {});

  useEffect(() => {
    if (propSC && Object.keys(propSC).length > 0) {
      setSc(propSC);
      return;
    }
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API}/site-config`);
        if (res.data) setSc(res.data);
      } catch (e) {
        // Fallback to static defaults
      }
    };
    fetchConfig();
  }, [propSC]);

  const phone = sc.local_seo_phone || "+91-9257059361";
  const email = sc.local_seo_email || "contactus@jamadhan.com";
  const address = sc.local_seo_street 
    ? `${sc.local_seo_street}, ${sc.local_seo_city || 'Jaipur'}, ${sc.local_seo_region || 'Rajasthan'} - ${sc.local_seo_postal || '302021'}`
    : "3rd Floor, JamaDhan Stock Market Institute, Ajmer Road, Opposite MG Motors, Heera Nagar, Tagore Nagar, Jaipur, Rajasthan, 302021";
  const rawCopyright = sc.footer_copyright || "JamaDhan Stock Market Institute. All rights reserved.";
  const copyright = rawCopyright.includes("Trading Academy")
    ? rawCopyright.replace(/Jamadhan Trading Academy/gi, "JamaDhan Stock Market Institute")
    : rawCopyright;

  const cities = [
    { label: "Jaipur", href: "/stock-market-course-in-jaipur/" },
    { label: "Jodhpur", href: "/stock-market-classes-in-jodhpur/" },
    { label: "Udaipur", href: "/stock-market-classes-in-udaipur/" },
    { label: "Kota", href: "/stock-market-classes-in-kota/" },
    { label: "Ajmer", href: "/stock-market-classes-in-ajmer/" },
    { label: "Sikar", href: "/stock-market-classes-in-sikar/" },
    { label: "Bhilwara", href: "/stock-market-classes-in-bhilwara/" },
  ];

  const quickLinks = [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about/" },
    { label: "Courses", href: "/#courses" },
    { label: "Drishti AI", href: "/drishti-ai/" },
    { label: "Gallery", href: "/gallery/" },
    { label: "Blogs", href: "/blog/" },
    { label: "Contact Us", href: "/contact/" },
  ];

  const trendingCourses = [
    { title: "Super Trader Edge Course", href: "/courses/technical-analysis-course/" },
    { title: "Super Option Analysis Course", href: "/courses/options-trading-course/" },
    { title: "Super Trader Combo", href: "/courses/technical-analysis-options-combo-course/" },
    { title: "Smart Trader Elementary Course (Free)", href: "/courses/smart-trader-elementary/" },
  ];

  const legalLinks = [
    { label: "Privacy Policy", href: "/privacy-policy/" },
    { label: "Terms & Conditions", href: "/terms-and-conditions/" },
    { label: "Refund & Cancellation", href: "/refund-and-cancellation-policy/" },
  ];

  return (
    <footer className="relative overflow-hidden border-t border-white/[0.08] bg-[#040508] text-[#CBD5E1] pt-16 pb-10 px-4 sm:px-6 lg:px-8" data-testid="global-footer">
      {/* Ambient background glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-[radial-gradient(circle,rgba(0,255,136,0.05),transparent_70%)] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-96 h-96 bg-[radial-gradient(circle,rgba(0,240,255,0.04),transparent_70%)] blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.02] [background-image:linear-gradient(rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:48px_48px]" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Main 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-12">
          
          {/* Col 1: Brand & Contact Info (5 cols on lg) */}
          <div className="lg:col-span-5 space-y-5">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.3)] border border-[#00FF88]/30 group-hover:border-[#00FF88] transition-colors p-0.5 bg-black">
                <img src="/jamadhan-logo.png" alt="Jamadhan Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-black text-white font-outfit tracking-tight group-hover:text-[#00FF88] transition-colors">
                  JAMADHAN
                </span>
                <span className="text-[10px] font-mono tracking-widest text-[#00F0FF] uppercase">Stock Market Institute</span>
              </div>
            </Link>

            <p className="text-sm text-[#94A3B8] max-w-md leading-relaxed">
              India's Premier Stock Market Learning Institute & AI-Powered Market Intelligence System. Empowering traders with institutional demand-supply zone strategies.
            </p>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] text-xs text-zinc-300 font-jetbrains">
              <ShieldCheck className="w-3.5 h-3.5 text-[#00FF88]" />
              <span>ISO 21001:2018 Certified Educational Organization</span>
            </div>

            {/* Address & Contact List */}
            <ul className="space-y-3 pt-2 text-sm text-[#CBD5E1]">
              <li className="flex items-start gap-3">
                <MapPin className="mt-1 h-4 w-4 shrink-0 text-[#00FF88]" />
                <span className="text-xs sm:text-sm leading-relaxed">{address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-[#00FF88]" />
                <a href={`tel:${phone.replace(/\s+/g, '')}`} className="text-xs sm:text-sm hover:text-[#00FF88] transition-colors">
                  {phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 shrink-0 text-[#00FF88]" />
                <a href={`mailto:${email}`} className="text-xs sm:text-sm hover:text-[#00FF88] transition-colors">
                  {email}
                </a>
              </li>
            </ul>

            {/* Social Icons Row */}
            <div className="flex items-center gap-2.5 pt-2">
              {[
                { label: "WhatsApp", href: `https://wa.me/${phone.replace(/[^0-9]/g, '')}`, color: "text-[#25D366] hover:border-[#25D366]/40", svg: <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.54 0 .22 5.32.22 11.86c0 2.09.55 4.13 1.59 5.93L.12 24l6.36-1.67a11.82 11.82 0 0 0 5.6 1.43h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.43-8.42ZM12.09 21.76h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.22-3.77.99 1.01-3.67-.24-.38a9.84 9.84 0 0 1-1.5-5.25C2.21 6.42 6.64 2 12.08 2c2.64 0 5.11 1.03 6.98 2.89a9.77 9.77 0 0 1 2.89 6.97c0 5.45-4.43 9.9-9.86 9.9Zm5.4-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.46-.88-.78-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.48.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" /></svg> },
                { label: "YouTube", href: sc.youtube_link || "https://www.youtube.com/@jamadhan", color: "text-[#FF0000] hover:border-[#FF0000]/40", icon: <Youtube className="h-4 w-4" /> },
                { label: "Instagram", href: sc.instagram_link || "https://www.instagram.com/jama_dhan/", color: "text-[#FF4D8D] hover:border-[#FF4D8D]/40", icon: <Instagram className="h-4 w-4" /> },
                { label: "Facebook", href: sc.social_facebook || "https://www.facebook.com/jamadhaninstitute", color: "text-[#1877F2] hover:border-[#1877F2]/40", icon: <Facebook className="h-4 w-4" /> },
                { label: "Telegram", href: TELEGRAM_LINK, color: "text-[#26A5E4] hover:border-[#26A5E4]/40", svg: <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0h-.056zm4.962 7.224c.451-.01.636.37.656.605.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.014-.15-.056-.212-.305-.188-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg> },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className={`flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] ${s.color} transition-all hover:-translate-y-1 hover:bg-white/[0.08]`}
                >
                  {s.svg || s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Col 2: Quick Links (2 cols on lg) */}
          <div className="lg:col-span-2">
            <h4 className="mb-4 font-outfit text-base font-bold text-white uppercase tracking-wider">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.href}
                    className="flex items-center gap-1.5 text-xs sm:text-sm text-zinc-400 hover:text-[#00FF88] transition-colors group"
                  >
                    <ChevronRight className="h-3.5 w-3.5 text-zinc-600 group-hover:text-[#00FF88] transition-colors" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Trending Courses (3 cols on lg) */}
          <div className="lg:col-span-3">
            <h4 className="mb-4 font-outfit text-base font-bold text-white uppercase tracking-wider">
              Trending Courses
            </h4>
            <ul className="space-y-2.5">
              {trendingCourses.map((c) => (
                <li key={c.title}>
                  <Link
                    to={c.href}
                    className="flex items-start gap-1.5 text-xs sm:text-sm text-zinc-400 hover:text-[#00FF88] transition-colors group leading-relaxed"
                  >
                    <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-600 group-hover:text-[#00FF88] transition-colors" />
                    <span>{c.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Legal & Policies (2 cols on lg) */}
          <div className="lg:col-span-2">
            <h4 className="mb-4 font-outfit text-base font-bold text-white uppercase tracking-wider">
              Legal
            </h4>
            <ul className="space-y-2.5">
              {legalLinks.map((item) => (
                <li key={item.label}>
                  <Link
                    to={item.href}
                    className="flex items-center gap-1.5 text-xs sm:text-sm text-zinc-400 hover:text-[#00FF88] transition-colors group"
                  >
                    <ChevronRight className="h-3.5 w-3.5 text-zinc-600 group-hover:text-[#00FF88] transition-colors" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* City Landing Pages Ribbon */}
        <div className="border-t border-white/[0.08] py-5 my-6">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-semibold text-zinc-300">
            <span className="text-zinc-500 uppercase tracking-widest text-[10px] font-mono mr-2">Classes In:</span>
            {cities.map((city, index) => (
              <div key={city.label} className="flex items-center gap-4">
                {index > 0 && <span className="hidden text-white/20 sm:inline">·</span>}
                <Link
                  to={city.href}
                  className="hover:text-[#00FF88] transition-colors"
                >
                  {city.label}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Copyright Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500 pt-4 border-t border-white/[0.04]">
          <p>© {new Date().getFullYear()} {copyright}</p>
          <div className="flex items-center gap-4 font-jetbrains text-[11px]">
            <a href="https://jamadhan.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#00FF88] transition-colors">
              jamadhan.com
            </a>
            <span>·</span>
            <span className="text-zinc-400">Jaipur, Rajasthan</span>
          </div>
        </div>

        {/* Regulatory & Risk Disclaimer */}
        <div className="pt-4 mt-4 border-t border-white/[0.04] text-[11px] leading-relaxed text-zinc-500 text-center sm:text-left">
          <p>
            JamaDhan is an educational institute that provides stock market training only. We are not registered with SEBI as an investment adviser or research analyst, and nothing on this website, in our classes or in our community groups is investment advice or a recommendation to buy, sell or hold any security. Trading and investing in securities, derivatives, commodities and currencies involve a high risk of loss, and securities market investments are subject to market risks. Examples shown are for teaching purposes only. Past performance and individual student experiences are not indicative of future results.
          </p>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
